import { useApi } from '@hooks/useApi';
import useAuth from '@hooks/useAuth';
import { useDebouncedValue } from '@hooks/useDebouncedValue';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FieldValues, useFormContext } from 'react-hook-form';
import type { ExtendedSlot } from '../Views/TableView/types';

interface Options {
    slot: ExtendedSlot;
    kiosk?: boolean;
    /** Set false to skip the rule check entirely (the assistant's showRuleChecker prop) */
    enabled?: boolean;
    endpoint?: string;
}

export interface BookingCheckResult {
    /** Blocking rule violations — while any of these stand, the booking cannot go through */
    errors: string[];
    /** A single message that replaces the error list when the booking is forbidden outright */
    forbidMessage?: string;
    /** Non-blocking notes the club attached to the rules */
    messages: string[];
    /** The selection would create a request instead of a booking: nothing is charged now */
    requiresApproval: boolean;
    /** Set by the OnlinePayment rule effect, undefined means the space setting decides */
    onlinePayments?: boolean;
    /** Mollie method the OnlinePayment rule effect forces on this booking */
    paymentMethod?: string;
    isLoading: boolean;
}

/**
 * Runs the server-side rule check for the booking currently in the form and hands back
 * its verdict, so the caller decides where to render it. Must be used inside the Form.
 */
export default function useBookingCheck({ slot, kiosk, enabled = true, endpoint }: Options): BookingCheckResult {
    const api = useApi();
    const { watch } = useFormContext();
    const _data: FieldValues = watch();
    const data = useDebouncedValue(_data, 500, true);
    const [result, setResult] = useState<Record<string, string> | undefined>();
    const [messages, setMessages] = useState<string[] | undefined>();
    const [requiresApproval, setRequiresApproval] = useState(false);
    const [onlinePayments, setOnlinePayments] = useState<boolean | undefined>();
    const [paymentMethod, setPaymentMethod] = useState<string | undefined>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { user } = useAuth();
    // aborts the in-flight check so a superseded request can't clobber fresh results
    const abortRef = useRef<AbortController | null>(null);

    // ein Urteil gilt nur für die Eingaben, zu denen es gehört — sobald es keins mehr gibt,
    // muss das alte weg, sonst hält es die Bestellung wegen längst geänderter Daten auf
    const clearVerdict = useCallback(() => {
        setResult(undefined);
        setMessages(undefined);
        setRequiresApproval(false);
        setOnlinePayments(undefined);
        setPaymentMethod(undefined);
    }, []);

    const fetchCheck = useCallback(() => {
        if (!enabled) return;
        if ((!user?.name || !user?.email) && (!data.user_name || !data.user_email)) {
            clearVerdict();
            return;
        }
        // cancel any check still running before starting the next one
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        // only the latest request may touch state — a stale/aborted one must not
        const isCurrent = () => abortRef.current === controller;
        setIsLoading(true);
        void api({
            url: endpoint ?? `space-manager/space/${slot.spaceId}/check`,
            method: 'POST',
            signal: controller.signal,
            // läuft alle 500ms im Hintergrund — was er zu sagen hat, gehört ins Rail, nicht in Toasts
            muted: true,
            data: {
                kiosk,
                user_name: user?.name,
                user_email: user?.email,
                ...data,
                // a member picked from a blurred list carries member_id instead of user_email,
                // so count it as a valid booker too — otherwise the min-fellows check misfires
                bookers: data.bookers?.filter(
                    (booker: { user_name: any; user_email: any; member_id: any }) =>
                        booker.user_name && (booker.user_email || booker.member_id)
                )
            },
            onSuccess: (data) => {
                if (!isCurrent()) return;
                setResult(data?.errors);
                setMessages(data?.messages);
                setRequiresApproval(Boolean(data?.requires_approval));
                setOnlinePayments(typeof data?.online_payments === 'boolean' ? data.online_payments : undefined);
                setPaymentMethod(data?.payment_method || undefined);
            },
            // ein 422 heißt: die Eingabe ist noch unvollständig (halb getippter Mitbucher o. Ä.),
            // nicht dass eine Regel verletzt ist. Die Feldfehler dürfen also nicht als Regelverstoß
            // ins Rail wandern — das Urteil ist bis zur nächsten Prüfung einfach offen
            onFailure: () => {
                if (isCurrent()) clearVerdict();
            },
            // clear loading only when the current request settles (success, error, or cancel)
            onFinish: () => {
                if (isCurrent()) setIsLoading(false);
            }
        });
    }, [data, slot, user, kiosk, enabled, endpoint, clearVerdict]);

    useEffect(() => {
        void fetchCheck();
        // abort the pending check when inputs change or the component unmounts
        return () => abortRef.current?.abort();
    }, [fetchCheck]);

    const forbidMessage = result?.forbid;

    return {
        // a forbid verdict speaks for itself; listing the individual rules next to it only repeats
        errors: forbidMessage ? [] : Object.values(result ?? {}).filter(Boolean),
        forbidMessage,
        messages: messages ?? [],
        requiresApproval,
        onlinePayments,
        paymentMethod,
        isLoading
    };
}
